import React, { useState, useRef, useEffect, useMemo, useCallback, Fragment } from 'react';
import TwemojiInput from './TwemojiInput';

import { TwemojiText } from '../../utils/appleEmoji';
import { fixFileName } from '../../utils/fixFileName';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Users, Send, Image, Plus, Paperclip, Search, X, ChevronUp, ChevronDown, Info, Reply, Share2, Copy, Trash2, CheckSquare, ArrowRight, Star, Bell, BellOff, Scissors, Clipboard, Flag } from 'lucide-react';
import EmojiPickerButton, { ReactionEmojiPicker } from './EmojiPickerButton';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import ImageModal from './ImageModal';
import ImagePreviewModal from './ImagePreviewModal';
import FileConfirmModal from './FileConfirmModal';
import MessageInfoModal from './MessageInfoModal';
import ForwardModal from './ForwardModal';
import ContextMenu from '../Common/ContextMenu';
import ReportMessageModal from './ReportMessageModal';
import DeleteModal from '../Common/DeleteModal';
import UserContextMenu from '../Common/UserContextMenu';
import UserAvatar from '../Common/UserAvatar';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import axios from 'axios';
import { getApiBaseUrl, groupApi, chatApi } from '../../services/cloudApi';
import { getSocket } from '../../services/socketService';
import { getPlusEmailColor } from '../../utils/userDisplay';
import renderGroupSystemMessage from '../../utils/renderGroupSystemMessage';
import Username, { getDisplayName } from '../Common/Username';
import GroupAvatar from './GroupAvatar';
import SharedNoteMessage from './SharedNoteMessage';
import CollabInviteMessage from './CollabInviteMessage';
import EventCardMessage from './EventCardMessage';
import PollCardMessage from './PollCardMessage';
import PollCreateModal from './PollCreateModal';
import ContactShareModal from './ContactShareModal';
import ContactCardMessage from './ContactCardMessage';
import ChatAttachmentPopover from './ChatAttachmentPopover';
import ChatImage from '../Common/ChatImage';
import NoteListModal from './NoteListModal';
import CreateEventModal from '../Calendar/CreateEventModal';
import SelectCalendarModal from '../Calendar/SelectCalendarModal';
import { useCalendar } from '../../contexts/CalendarContext';
import { useMembership } from '../../hooks/useMembership';
import { UPGRADE_MESSAGES } from '../../utils/membershipConstants';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const QUICK_REACTIONS = ['😂', '😮', '😢', '😍', '👍'];

// Helper function to get user initials
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

// Helper function to clean message text
const cleanMessageText = (text, maxLength = 100) => {
  if (!text) return '';
  const stripped = text.replace(/<[^>]*>/g, '');
  const decoded = stripped
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  if (decoded.length > maxLength) {
    return decoded.substring(0, maxLength) + '...';
  }
  return decoded;
};

// Helper to safely render message text with clickable URLs and optional search highlight
const renderMessageContent = (text, searchQuery, isOwnMessage) => {
  if (!text && text !== 0) return null;

  const safeText = String(text);
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = safeText.split(urlRegex);

  const renderWithHighlight = (segment) => {
    const query = (searchQuery || '').trim();
    if (!query) {
      return segment;
    }

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const highlightRegex = new RegExp(`(${escapedQuery})`, 'gi');

    return segment.split(highlightRegex).map((part, index) => {
      if (!part) return null;
      const isMatch = part.toLowerCase() === query.toLowerCase();
      if (!isMatch) {
        return <span key={index}>{part}</span>;
      }
      return (
        <mark
          key={index}
          className={`${isOwnMessage ? 'bg-yellow-400/30 text-white' : 'bg-yellow-300 text-gray-900'} px-0.5 rounded`}
        >
          {part}
        </mark>
      );
    });
  };

  return parts.map((part, index) => {
    if (!part) return null;

    const isUrl = index % 2 === 1;
    if (isUrl) {
      const url = part;
      return (
        <a
          key={`url-${index}-${url}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-blue-600 dark:text-blue-400 break-words"
        >
          {url}
        </a>
      );
    }

    return <TwemojiText key={`text-${index}`} text={part} />;
  });
};

const GroupChatView = ({ group, onClose, onShowGroupInfo, onContactOpenFriendInfo, onContactAddFriend }) => {
  const { t, i18n } = useTranslation('chat');
  const dateLocale = getDateFnsLocale(i18n.language);

  // Helper function to format message time
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMins = Math.floor((now - date) / 60000);
      const isToday = date.toDateString() === now.toDateString();

      if (diffMins < 1) return t('groupChatView.justNow');
      if (isToday) {
        return format(date, 'HH:mm', { locale: dateLocale });
      }
      return format(date, 'd MMM HH:mm', { locale: dateLocale });
    } catch (error) {
      return '';
    }
  };

  const {
    selectedGroupMessages, 
    selectedGroupId,
    sendGroupMessage,
    groupTyping,
    groupMessageReads,
    replyToMessage,
    setReplyToMessage,
    forwardMessage,
    setForwardMessage,
    loadGroupMessages,
    removeGroupMessage,
    updateGroupMessage,
  } = useChat();
  const { user, authToken, isPlusUser } = useAuth();
  const [messageText, setMessageText] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [textAreaContextMenu, setTextAreaContextMenu] = useState({ isOpen: false, x: 0, y: 0, hasSelection: false });
  const [messageContextMenu, setMessageContextMenu] = useState({ isOpen: false, x: 0, y: 0, message: null });
  const [reportModal, setReportModal] = useState({ isOpen: false, messageId: null });
  const [deleteFlow, setDeleteFlow] = useState({ chooseOpen: false, confirmOpen: false, message: null, deleteType: null });
  const [messageReactions, setMessageReactions] = useState({});
  const [reactionPickerState, setReactionPickerState] = useState({ isOpen: false, x: 0, y: 0, messageId: null });
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedImageFileName, setSelectedImageFileName] = useState(null);
  const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [showFileConfirmModal, setShowFileConfirmModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchMatchIndex, setSearchMatchIndex] = useState(-1);
  const [showMessageInfoModal, setShowMessageInfoModal] = useState(false);
  const [selectedMessageForInfo, setSelectedMessageForInfo] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [showAttachmentPopover, setShowAttachmentPopover] = useState(false);
  const [showNoteListModal, setShowNoteListModal] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showSelectCalendarModal, setShowSelectCalendarModal] = useState(false);
  const [selectedCalendarForEvent, setSelectedCalendarForEvent] = useState(null);
  const [showPollCreateModal, setShowPollCreateModal] = useState(false);
  const [showContactShareModal, setShowContactShareModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const attachmentTriggerRef = useRef(null);
  const { createEvent, calendars, selectedCalendar } = useCalendar();
  const [mentionedUsers, setMentionedUsers] = useState([]); // For @mention autocomplete
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const searchInputRef = useRef(null);
  const messageRefs = useRef({});
  const messagesContainerRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);

  const groupTypingTimeoutRef = useRef(null);
  const isGroupTypingActiveRef = useRef(false);
  const [groupMembers, setGroupMembers] = useState([]); // Group members for @mention

  // Load group members for @mention
  const loadMembers = useCallback(async () => {
    if (!group?.id || !authToken) return;
    try {
      const result = await groupApi.getGroup({ token: authToken, groupId: group.id });
      if (result?.ok && result?.group?.members) {
        setGroupMembers(result.group.members);
      }
    } catch (error) {
      console.error('Load group members error:', error);
    }
  }, [group?.id, authToken]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Re-load members when a role changes in this group
  useEffect(() => {
    const handleRoleChanged = (e) => {
      if (e.detail?.groupId === group?.id) {
        loadMembers();
      }
    };
    window.addEventListener('notesplus-group-role-changed', handleRoleChanged);
    return () => window.removeEventListener('notesplus-group-role-changed', handleRoleChanged);
  }, [group?.id, loadMembers]);

  // Get typing users for this group
  const currentTypingUsers = groupTyping[group?.id] || [];

  // Check if group is locked (from group prop or socket events)
  const [groupLockedStatus, setGroupLockedStatus] = useState(null);

  // Listen to socket events for group lock/unlock/destroy
  useEffect(() => {
    if (!selectedGroupId) return;

    const socket = getSocket();
    if (!socket || !socket.connected) return;

    const handleGroupLocked = (data) => {
      if (data.groupId === selectedGroupId) {
        setGroupLockedStatus({
          locked: true,
          message: data.message || t('groupChatView.lockedMessageSocket'),
        });
      }
    };

    const handleGroupUnlocked = (data) => {
      if (data.groupId === selectedGroupId) {
        // Clear locked status when group is unlocked
        setGroupLockedStatus(null);
        
        // Also update group state to reflect unlock
        // This ensures persistence across page refreshes
        if (group?.isLocked === true) {
          // Group will be updated via loadGroups when backend confirms unlock
        }
      }
    };

    const handleGroupDestroyed = (data) => {
      if (data.groupId === selectedGroupId) {
        setGroupLockedStatus({
          locked: true,
          destroyed: true,
          message: t('groupChatView.destroyedMessageSocket'),
        });
      }
    };

    socket.on('group:locked', handleGroupLocked);
    socket.on('group:unlocked', handleGroupUnlocked);
    socket.on('group:destroyed', handleGroupDestroyed);

    return () => {
      socket.off('group:locked', handleGroupLocked);
      socket.off('group:unlocked', handleGroupUnlocked);
      socket.off('group:destroyed', handleGroupDestroyed);
    };
  }, [selectedGroupId]);

  // Check if group is locked or destroyed (from state or group prop)
  // Priority: socket event > backend flag > group prop
  const isGroupLocked = groupLockedStatus?.locked === true || group?.isLocked === true;
  const isGroupDestroyed = groupLockedStatus?.destroyed === true || group?.is_active === false;
  
  // Initialize groupLockedStatus from backend if group is locked (for page refresh persistence)
  useEffect(() => {
    if (group?.isLocked === true && !groupLockedStatus) {
      setGroupLockedStatus({
        locked: true,
        message: group?.lockMessage || t('groupChatView.lockedMessageInit'),
      });
    } else if (group?.isLocked === false && groupLockedStatus?.locked === true && !groupLockedStatus?.destroyed) {
      // If backend says group is not locked but state says it is, reset (unless destroyed)
      setGroupLockedStatus(null);
    }
  }, [group?.isLocked, group?.lockMessage, groupLockedStatus]);

  // Filter messages by search query
  const searchMatches = useMemo(() => {
    if (!messageSearchQuery.trim() || !selectedGroupMessages) return [];
    const query = messageSearchQuery.toLowerCase();
    const matches = selectedGroupMessages
      .map((msg, index) => ({
        message: msg,
        index,
      }))
      .filter(({ message }) => message.content.toLowerCase().includes(query));
    
    return matches.reverse();
  }, [selectedGroupMessages, messageSearchQuery]);

  // Track if user has scrolled up
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);

  // Check if user scrolled up
  useEffect(() => {
    if (!messagesContainerRef.current) return;
    
    const container = messagesContainerRef.current;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setIsUserScrolledUp(!isAtBottom);
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (!messagesEndRef.current) return;
    
    const currentMessagesLength = selectedGroupMessages?.length || 0;
    const prevLength = prevMessagesLengthRef.current;
    
    if (currentMessagesLength > prevLength && !isUserScrolledUp) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
    prevMessagesLengthRef.current = currentMessagesLength;
  }, [selectedGroupMessages, isUserScrolledUp]);

  // Reset scroll state when group changes
  useEffect(() => {
    setIsUserScrolledUp(false);
    setReplyToMessage(null);
  }, [selectedGroupId, setReplyToMessage]);

  // When switching group chat: scroll to bottom instantly (no animation) so latest messages are shown immediately
  const didInstantScrollForGroupRef = useRef(null);
  const mountedGroupMsgIds = useRef(new Set());
  useEffect(() => {
    if (!selectedGroupId) return;
    didInstantScrollForGroupRef.current = selectedGroupId;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          const el = messagesContainerRef.current;
          el.scrollTop = el.scrollHeight;
        }
      });
    });
    return () => cancelAnimationFrame(id);
  }, [selectedGroupId]);

  // If messages load async after switching group, scroll to bottom once when they appear
  useEffect(() => {
    if (!selectedGroupId || !messagesContainerRef.current) return;
    const messageCount = selectedGroupMessages?.length ?? 0;
    if (messageCount === 0) return;
    if (didInstantScrollForGroupRef.current !== selectedGroupId) return;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          const el = messagesContainerRef.current;
          el.scrollTop = el.scrollHeight;
        }
        didInstantScrollForGroupRef.current = null;
      });
    });
    return () => cancelAnimationFrame(id);
  }, [selectedGroupId, selectedGroupMessages?.length]);

  // Handle @mention in input
  const handleTextChange = (newValue, cursorPosition) => {
    if (isGroupLocked || isGroupDestroyed) return; // Don't allow typing if group is locked or destroyed

    setMessageText(newValue);
    setIsComposing(newValue.trim().length > 0);

    // Check for @mention
    const textBeforeCursor = newValue.substring(0, cursorPosition ?? newValue.length);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's a space after @ (mention ended)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        // Show mention list
        setMentionQuery(textAfterAt.toLowerCase());
        setMentionStartIndex(lastAtIndex);
        setShowMentionList(true);
      } else {
        setShowMentionList(false);
      }
    } else {
      setShowMentionList(false);
    }

    // Handle group typing indicator (only if group is not locked or destroyed)
    if (!selectedGroupId || isGroupLocked || isGroupDestroyed) {
      // Stop typing if group is locked/destroyed
      if (isGroupTypingActiveRef.current) {
        const socket = getSocket();
        if (socket && socket.connected) {
          socket.emit('group:typing:stop', { groupId: selectedGroupId });
        }
        isGroupTypingActiveRef.current = false;
      }
      if (groupTypingTimeoutRef.current) {
        clearTimeout(groupTypingTimeoutRef.current);
        groupTypingTimeoutRef.current = null;
      }
      return;
    }

    const socket = getSocket();
    if (!socket || !socket.connected) return;

    // Clear existing timeout
    if (groupTypingTimeoutRef.current) {
      clearTimeout(groupTypingTimeoutRef.current);
    }

    // If typing started
    if (!isGroupTypingActiveRef.current && newValue.trim().length > 0) {
      socket.emit('group:typing:start', { groupId: selectedGroupId });
      isGroupTypingActiveRef.current = true;
    }

    // Set timeout to stop typing after 2 seconds
    groupTypingTimeoutRef.current = setTimeout(() => {
      if (isGroupTypingActiveRef.current) {
        socket.emit('group:typing:stop', { groupId: selectedGroupId });
        isGroupTypingActiveRef.current = false;
      }
      groupTypingTimeoutRef.current = null;
    }, 2000);
  };

  // Handle mention selection
  const handleMentionSelect = (member) => {
    if (!inputRef.current || mentionStartIndex === -1) return;

    const textBefore = messageText.substring(0, mentionStartIndex);
    const cursorStart = inputRef.current.getSelectionStart?.() ?? messageText.length;
    const textAfter = messageText.substring(cursorStart);
    const mentionText = `@${getDisplayName(member)} `;
    const newText = textBefore + mentionText + textAfter;
    const newCursorPos = textBefore.length + mentionText.length;

    setMessageText(newText);
    setShowMentionList(false);
    setMentionQuery('');
    setMentionStartIndex(-1);

    // Store mentioned user IDs for sending
    setMentionedUsers((prev) => {
      if (!prev.find(m => m.id === member.id)) {
        return [...prev, { id: member.id, name: getDisplayName(member) }];
      }
      return prev;
    });

    // Focus input and set cursor position
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setValue(newText, newCursorPos);
      }
    }, 0);
  };

  // Filter members for mention autocomplete
  const filteredMembersForMention = useMemo(() => {
    if (!showMentionList || !mentionQuery) return [];
    
    return groupMembers
      .filter(member => {
        const name = getDisplayName(member).toLowerCase();
        const email = (member.email || '').toLowerCase();
        const query = mentionQuery.toLowerCase();
        return (name.includes(query) || email.includes(query)) && member.id !== user?.id;
      })
      .slice(0, 5); // Max 5 suggestions
  }, [groupMembers, mentionQuery, showMentionList, user?.id]);

  // Handle send message
  const handleSendMessage = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!messageText.trim() || !selectedGroupId || isGroupLocked || isGroupDestroyed) return;

    // Stop typing
    const socket = getSocket();
    if (socket && socket.connected && isGroupTypingActiveRef.current) {
      socket.emit('group:typing:stop', { groupId: selectedGroupId });
      isGroupTypingActiveRef.current = false;
    }
    if (groupTypingTimeoutRef.current) {
      clearTimeout(groupTypingTimeoutRef.current);
      groupTypingTimeoutRef.current = null;
    }

    // Extract mentioned user IDs from message text
    const mentionedUserIds = mentionedUsers.map(u => u.id);

    // Send message
    const replyToId = replyToMessage?.id || null;
    sendGroupMessage(
      selectedGroupId, 
      messageText, 
      'text', 
      null, 
      null, 
      replyToId, 
      null, 
      mentionedUserIds
    );
    
    setMessageText('');
    setIsComposing(false);
    setMentionedUsers([]);
    setShowMentionList(false);
    setReplyToMessage(null);

    // Clear TwemojiInput and focus
    inputRef.current?.clear();
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleKeyDown = (e) => {
    // Handle mention list navigation
    if (showMentionList && filteredMembersForMention.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        e.preventDefault();
        // TODO: Implement mention list navigation
        return;
      }
    }

    // Enter without Shift sends the message; mark as handled so TwemojiInput skips its own Enter handler
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const { limits } = useMembership();
  const maxUploadBytes = limits?.maxUploadBytes ?? 15 * 1024 * 1024;

  const showUploadToast = (type, title, message) => {
    window.dispatchEvent(
      new CustomEvent('notesplus-show-toast', {
        detail: { type, title, message },
      })
    );
  };

  const uploadWithType = useCallback(async (uploadType, file, options = {}) => {
    if (!selectedGroupId || !authToken || !file || isGroupLocked || isGroupDestroyed) return null;
    if (file.size > maxUploadBytes) {
      showUploadToast('error', t('groupChatView.uploadSizeLimitTitle'), UPGRADE_MESSAGES.uploadLimit);
      return null;
    }
    const { caption } = options;
    setUploadingFile(true);
    setUploadProgress(0);
    try {
      const apiBaseUrl = await getApiBaseUrl();
      const url = `${apiBaseUrl}/chat/upload?type=${uploadType}`;
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        },
      });
      if (!data?.ok || !data?.fileUrl) {
        showUploadToast('error', t('common:errorTitle'), data?.message || t('groupChatView.uploadFailedFallback'));
        return null;
      }
      const relativeFileUrl = data.fileUrl.startsWith('/') ? data.fileUrl : `/${data.fileUrl}`;
      if (uploadType === 'image') {
        const messageContent = (caption && caption.trim()) || data.fileName || file.name;
        await sendGroupMessage(selectedGroupId, messageContent, 'image', relativeFileUrl, data.fileName);
      } else {
        await sendGroupMessage(selectedGroupId, data.fileName || file.name, 'file', relativeFileUrl, data.fileName);
      }
      setTimeout(() => inputRef.current?.focus(), 100);
      return data;
    } catch (err) {
      const serverMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        t('groupChatView.fileUploadErrorFallback');
      const title = err.response?.status === 413 ? t('groupChatView.uploadSizeLimitTitle') : t('groupChatView.sendErrorTitle');
      showUploadToast('error', title, serverMessage);
      return null;
    } finally {
      setUploadProgress(null);
      setUploadingFile(false);
    }
  }, [selectedGroupId, authToken, sendGroupMessage, isGroupLocked, isGroupDestroyed, maxUploadBytes]);

  const handleFileSelect = async (type) => {
    if (!selectedGroupId || !authToken || isGroupLocked || isGroupDestroyed) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'image'
      ? 'image/*'
      : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.txt,.jpg,.jpeg,.png,.gif,.webp,.heic';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (type === 'image') {
        if (file.size > maxUploadBytes) {
          showUploadToast('error', t('groupChatView.uploadSizeLimitTitle'), UPGRADE_MESSAGES.uploadLimit);
          return;
        }
        setSelectedImageFile(file);
        setShowImagePreviewModal(true);
        return;
      }
      if (file.size > maxUploadBytes) {
        showUploadToast('error', t('groupChatView.uploadSizeLimitTitle'), UPGRADE_MESSAGES.uploadLimit);
        return;
      }
      setPendingFile(file);
      setShowFileConfirmModal(true);
    };
    input.click();
  };

  const handleSendImage = async (imageFile, caption) => {
    if (!selectedGroupId || !authToken || !imageFile || isGroupLocked || isGroupDestroyed) return;
    if (imageFile.size > maxUploadBytes) {
      showUploadToast('error', t('groupChatView.uploadSizeLimitTitle'), UPGRADE_MESSAGES.uploadLimit);
      return;
    }
    await uploadWithType('image', imageFile, { caption });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!isGroupLocked && !isGroupDestroyed) setIsDraggingOver(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    if (isGroupLocked || isGroupDestroyed || !selectedGroupId || !authToken) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.size > maxUploadBytes) {
      showUploadToast('error', t('groupChatView.uploadSizeLimitTitle'), UPGRADE_MESSAGES.uploadLimit);
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'];
    if (file.type.startsWith('image/') || imageExts.includes(ext)) {
      setSelectedImageFile(file);
      setShowImagePreviewModal(true);
    } else {
      setPendingFile(file);
      setShowFileConfirmModal(true);
    }
  };

  // Get message read status
  const getMessageReadStatus = (messageId) => {
    const readStatus = groupMessageReads[messageId];
    if (!readStatus) {
      return { readCount: 0, totalMembers: groupMembers.length, allRead: false };
    }
    return readStatus;
  };

  // Get sender name for message
  const getSenderName = (message) => {
    if (String(message.senderId) === String(user?.id)) return t('groupChatView.youLabel');
    const sender = groupMembers.find(m => String(m.id) === String(message.senderId));
    if (sender) return getDisplayName(sender);
    // Message-embedded fallback (late-added members or history loads)
    if (message.senderName) return message.senderName;
    if (message.senderEmail) return message.senderEmail.split('@')[0];
    return t('groupChatView.unknownUserFallback');
  };

  // Get sender avatar for message
  const getSenderAvatar = (message) => {
    if (String(message.senderId) === String(user?.id)) return user?.avatarUrl || null;
    const sender = groupMembers.find(m => String(m.id) === String(message.senderId));
    if (sender?.avatarUrl) return sender.avatarUrl;
    // Fallback to backend-embedded avatar (sent in socket payload + API response)
    if (message.senderAvatarUrl) return message.senderAvatarUrl;
    return null;
  };

  // Get sender object with Plus status for message
  const getSenderWithPlus = (message) => {
    if (String(message.senderId) === String(user?.id)) return user;
    const sender = groupMembers.find(m => String(m.id) === String(message.senderId));
    if (sender) {
      // isPlus is determined by the global user role (userRole), not the group role
      const senderIsPlus = message.senderIsPlus !== undefined
        ? message.senderIsPlus
        : (sender?.isPlus || sender?.userRole === 'plus' || sender?.userRole === 'admin');
      return { ...sender, isPlus: senderIsPlus };
    }
    const senderIsPlus = message.senderIsPlus || false;
    return {
      id: message.senderId,
      email: message.senderEmail,
      name: message.senderName || (message.senderEmail ? message.senderEmail.split('@')[0] : t('groupChatView.unknownFallback')),
      avatarUrl: message.senderAvatarUrl || null,
      isPlus: senderIsPlus,
    };
  };

  // Mark group message as read
  const markGroupMessageAsRead = useCallback((messageId) => {
    if (!selectedGroupId || !authToken || !messageId) return;
    
    const socket = getSocket();
    if (socket && socket.connected) {
      socket.emit('group:message:read', { groupId: selectedGroupId, message_id: messageId });
    }
  }, [selectedGroupId, authToken]);

  // Mark messages as read when viewing
  useEffect(() => {
    if (!selectedGroupId || !selectedGroupMessages) return;
    
    const unreadMessages = selectedGroupMessages.filter(msg => 
      msg.senderId !== user?.id && 
      msg.type !== 'system' && // System messages should NOT be marked as read
      !groupMessageReads[msg.id]?.readers?.includes(user?.id)
    );
    
    // Mark as read with debounce
    const timeout = setTimeout(() => {
      unreadMessages.forEach(msg => {
        markGroupMessageAsRead(msg.id);
      });
    }, 500);
    
    return () => clearTimeout(timeout);
  }, [selectedGroupId, selectedGroupMessages, user?.id, groupMessageReads, markGroupMessageAsRead]);

  // Format typing indicator text
  const typingIndicatorText = useMemo(() => {
    if (currentTypingUsers.length === 0) return null;
    if (currentTypingUsers.length === 1) {
      // currentTypingUsers contains { userId, userName, avatarUrl }
      const typingUser = currentTypingUsers[0];
      // Try to find user in group members first (for nickname support)
      const member = groupMembers.find(m => m.id === typingUser.userId || m.user_id === typingUser.userId);
      const displayName = member
        ? getDisplayName(member)
        : (typingUser?.userName || t('groupChatView.unknownFallback'));
      return t('groupChatView.typingOne', { name: displayName });
    }
    return t('groupChatView.typingMany', { count: currentTypingUsers.length });
  }, [currentTypingUsers, groupMembers, t]);

  // Handle search
  const handleSearchNext = () => {
    if (searchMatches.length === 0) return;
    setSearchMatchIndex((prev) => (prev + 1) % searchMatches.length);
  };

  const handleSearchPrevious = () => {
    if (searchMatches.length === 0) return;
    setSearchMatchIndex((prev) => (prev - 1 + searchMatches.length) % searchMatches.length);
  };

  const handleSearchClose = () => {
    setShowSearchBar(false);
    setMessageSearchQuery('');
    setSearchMatchIndex(-1);
  };

  // Context menu handlers
  const handleAddReaction = (emoji, msgId) => {
    const id = msgId || messageContextMenu.message?.id;
    if (!id) return;
    setMessageReactions(prev => {
      const cur = { ...(prev[id] || {}) };
      if (cur[emoji]) {
        const newCount = cur[emoji] - 1;
        if (newCount <= 0) delete cur[emoji];
        else cur[emoji] = newCount;
      } else {
        cur[emoji] = 1;
      }
      return { ...prev, [id]: cur };
    });
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
    setReactionPickerState({ isOpen: false, x: 0, y: 0, messageId: null });
  };

  const handleCopyMessage = () => {
    if (messageContextMenu.message && messageContextMenu.message.type !== 'system') {
      const textToCopy = messageContextMenu.message.type === 'text'
        ? messageContextMenu.message.content
        : messageContextMenu.message.file_name || messageContextMenu.message.content || t('groupChatView.messageDefaultLabel');
      navigator.clipboard.writeText(textToCopy).catch(err => console.error('Copy failed:', err));
    }
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  const handleReplyMessage = (directMessage = null) => {
    const message = directMessage || messageContextMenu.message;
    if (message && message.type !== 'system' && !message.is_deleted_for_everyone) {
      const senderWithPlus = getSenderWithPlus(message);
      const senderName = getSenderName(message);
      const messageText = message.type === 'text'
        ? cleanMessageText(message.content, 150)
        : (message.file_name || message.content || t('groupChatView.fileDefaultName'));
      setReplyToMessage({
        id: message.id,
        text: messageText,
        senderName: senderName,
        sender: senderWithPlus,
      });
    }
    if (!directMessage) {
      setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
    }
  };

  const handleFileDownload = async (fileUrl, fileName) => {
    try {
      const res = await fetch(fileUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fixFileName(fileName || t('groupChatView.fileDefaultName').toLowerCase());
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(fileUrl, '_blank');
    }
  };

  const handleForwardMessage = () => {
    if (messageContextMenu.message && messageContextMenu.message.type !== 'system') {
      const message = messageContextMenu.message;
      const senderName = getSenderName(message);
      const messageText = message.type === 'text' 
        ? cleanMessageText(message.content, 200)
        : (message.file_name || message.content || t('groupChatView.fileDefaultName'));
      
      setForwardMessage({
        id: message.id,
        text: messageText,
        senderName: senderName,
        type: message.type || 'text',
        content: message.content,
        file_url: message.file_url,
        fileUrl: message.file_url,
        file_name: message.file_name,
      });
      
      setShowForwardModal(true);
    }
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  const handleShowMessageInfo = () => {
    if (messageContextMenu.message && messageContextMenu.message.type !== 'system') {
      setSelectedMessageForInfo(messageContextMenu.message);
      setShowMessageInfoModal(true);
    }
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  const openDeleteFlow = () => {
    const msg = messageContextMenu.message;
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
    if (!msg) return;
    setDeleteFlow({ chooseOpen: true, confirmOpen: false, message: msg, deleteType: null });
  };

  const chooseDeleteType = (type) => {
    setDeleteFlow((prev) => ({ ...prev, chooseOpen: false, confirmOpen: true, deleteType: type }));
  };

  const handleConfirmDelete = async () => {
    const { message: msg, deleteType } = deleteFlow;
    setDeleteFlow({ chooseOpen: false, confirmOpen: false, message: null, deleteType: null });
    if (!msg || !authToken || !selectedGroupId) return;
    try {
      await chatApi.deleteGroupMessage({ token: authToken, groupId: selectedGroupId, messageId: msg.id, deleteType });
      if (deleteType === 'for_me') {
        removeGroupMessage(selectedGroupId, msg.id);
      } else {
        updateGroupMessage(selectedGroupId, msg.id, {
          is_deleted_for_everyone: true,
          deleted_by_name: user?.name || user?.username || t('groupChatView.youLabel'),
        });
      }
    } catch {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('common:errorTitle'), message: t('groupChatView.deleteFailedMessage') },
      }));
    }
  };

  const handleStarMessage = async () => {
    if (!messageContextMenu.message || !selectedGroupId || !authToken) return;
    
    const message = messageContextMenu.message;
    const isStarred = message.is_starred === true || message.starred === true;
    
    try {
      const result = await groupApi.starGroupMessage({
        token: authToken,
        groupId: selectedGroupId,
        messageId: message.id,
        starred: !isStarred,
      });
      
      if (result?.ok) {
        // Reload messages to update starred status
        if (loadGroupMessages) {
          await loadGroupMessages(selectedGroupId);
        }
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: isStarred ? t('groupChatView.starRemovedTitle') : t('groupChatView.starAddedTitle'),
              message: isStarred ? t('groupChatView.starRemovedMessage') : t('groupChatView.starAddedMessage'),
            },
          })
        );
      }
    } catch (error) {
      console.error('Star/unstar message error:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('common:errorTitle'),
            message: t('groupChatView.starErrorMessage'),
          },
        })
      );
    }
    
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  // Cleanup typing on unmount
  useEffect(() => {
    return () => {
      if (groupTypingTimeoutRef.current) {
        clearTimeout(groupTypingTimeoutRef.current);
      }
      if (isGroupTypingActiveRef.current && selectedGroupId) {
        const socket = getSocket();
        if (socket && socket.connected) {
          socket.emit('group:typing:stop', { groupId: selectedGroupId });
        }
        isGroupTypingActiveRef.current = false;
      }
    };
  }, [selectedGroupId]);

  // ESC key handler - Close group chat
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if any modal is open
      const isModalOpen = showImageModal || showImagePreviewModal || showFileConfirmModal || showMessageInfoModal || showForwardModal;
      
      // If a modal is open, let the modal handle ESC (they have their own handlers)
      if (isModalOpen) {
        return;
      }

      // If search bar is open, close it first
      if (showSearchBar && event.key === 'Escape') {
        handleSearchClose();
        return;
      }

      // If group chat is open and no modals/search, close group chat
      if (event.key === 'Escape' && selectedGroupId && onClose) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedGroupId, showImageModal, showImagePreviewModal, showFileConfirmModal, showMessageInfoModal, showForwardModal, showSearchBar, handleSearchClose, onClose]);

  if (!group) return null;

  return (
    <div
      className="h-full flex flex-col overflow-hidden bg-gray-50 dark:bg-[var(--color-bg-base)] relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay - WhatsApp style */}
      <AnimatePresence>
        {isDraggingOver && !isGroupLocked && !isGroupDestroyed && !showImagePreviewModal && !showFileConfirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[100005] flex items-center justify-center rounded-xl backdrop-blur-sm"
 style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          >
            <div className="text-center text-white drop-shadow-lg">
              <p className="text-lg font-medium">{t('groupChatView.dropFileHere')}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Group Chat Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-white/10 bg-white/90 dark:bg-[var(--color-bg-base)] backdrop-blur-sm">
        <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={onShowGroupInfo}>
          {/* Group Avatar */}
          <div className="relative flex-shrink-0">
            <GroupAvatar group={group} size="md" />
            {/* Plus badge if Plus group */}
            {group.is_plus_group && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center border-2 border-slate-200 dark:border-white/10">
                <Star className="w-2.5 h-2.5 text-white fill-white" />
              </div>
            )}
          </div>
          
          {/* Group Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{group.name}</h3>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              {typingIndicatorText ? (
                <span className="truncate italic">{typingIndicatorText}</span>
              ) : (
                <span className="truncate">
                  {groupMembers.length > 0 
                    ? groupMembers.slice(0, 3).map(m => getDisplayName(m)).join(', ') + 
                      (groupMembers.length > 3 ? ` +${groupMembers.length - 3}` : '')
                    : t('groupChatView.membersCountSuffix', { count: groupMembers.length })}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Header Actions */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {/* Group Info Toggle */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onShowGroupInfo}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-[var(--color-text-secondary)] hover:text-gray-900 dark:hover:text-white transition-colors"
            title={t('groupChatView.groupInfoTitle')}
          >
            <Info className="w-5 h-5" />
          </motion.button>

          {/* Message Search */}
          {showSearchBar ? (
            <div className="flex items-center gap-2 bg-white dark:bg-[var(--color-bg-elevated)] rounded-xl px-3 py-2 border border-gray-200 dark:border-white/10">
              <Search className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={messageSearchQuery}
                onChange={(e) => setMessageSearchQuery(e.target.value)}
                placeholder={t('groupChatView.searchPlaceholder')}
                className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#c8d8eb] min-w-0"
              />
              {messageSearchQuery.trim() && searchMatches.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>{searchMatchIndex + 1}/{searchMatches.length}</span>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={handleSearchPrevious}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[#24354f]/50"
                      title={t('groupChatView.previousMatch')}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={handleSearchNext}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[#24354f]/50"
                      title={t('groupChatView.nextMatch')}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
              <button
                onClick={handleSearchClose}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-500 dark:text-[var(--color-text-secondary)]"
                title={t('groupChatView.closeSearch')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowSearchBar(true)}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-[var(--color-text-secondary)] hover:text-gray-900 dark:hover:text-white transition-colors"
              title={t('groupChatView.searchMessages')}
            >
              <Search className="w-5 h-5" />
            </motion.button>
          )}
          
          {/* Group Info */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onShowGroupInfo}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-[var(--color-text-secondary)] hover:text-gray-900 dark:hover:text-white transition-colors"
            title={t('groupChatView.groupInfoAlt')}
          >
            <Info className="w-5 h-5" />
          </motion.button>
          
          {/* Close Chat */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-[var(--color-text-secondary)] hover:text-gray-900 dark:hover:text-white transition-colors"
            title={t('groupChatView.closeChat')}
          >
            <X className="w-5 h-5" />
          </motion.button>
        </div>
      </div>

      {/* Messages List */}
      <div 
        ref={messagesContainerRef} 
        className="chat-scroll-area flex-1 overflow-y-auto p-4 space-y-4 relative z-0 allow-text-selection bg-gray-50 dark:bg-[var(--color-bg-base)]"
      >
        <AnimatePresence>
          {!selectedGroupMessages || selectedGroupMessages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-500 dark:text-[var(--color-text-muted)]">
                <p className="text-sm">{t('groupChatView.noMessagesYet')}</p>
                <p className="text-xs mt-1">{t('groupChatView.writeFirstMessage')}</p>
              </div>
            </div>
          ) : (
            Array.isArray(selectedGroupMessages) && selectedGroupMessages.map((message, index) => {
              // Safety check: ensure message is an object with required fields
              if (!message || typeof message !== 'object' || !message.id) {
                return null;
              }
              
              // Date separator - show if this message is from a different day than previous
              const messageDate = new Date(message.timestamp || message.created_at);
              const prevMessage = index > 0 ? selectedGroupMessages[index - 1] : null;
              const prevMessageDate = prevMessage ? new Date(prevMessage.timestamp || prevMessage.created_at) : null;
              const showDateSeparator = !prevMessageDate || 
                messageDate.toDateString() !== prevMessageDate.toDateString();
              
              // System message - WhatsApp-style (centered, italic, light gray)
              if (message.type === 'system') {
                return (
                  <React.Fragment key={message.id}>
                    {showDateSeparator && (
                      <div className="flex justify-center items-center my-4">
                        <div className="px-3 py-1 bg-white/80 dark:bg-[var(--color-bg-elevated)] rounded-full border border-slate-200 dark:border-white/10 shadow-sm">
                          <p className="text-xs text-gray-500 dark:text-[var(--color-text-secondary)] font-medium">
                            {format(messageDate, 'dd.MM.yyyy', { locale: dateLocale })}
                          </p>
                        </div>
                      </div>
                    )}
                    <motion.div
                      data-message-id={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex justify-center items-center my-2"
                    >
                      <div className="px-3 py-1.5 bg-transparent">
                        <p className="text-xs text-gray-400 dark:text-gray-500 italic text-center">
                          {renderGroupSystemMessage(message)}
                        </p>
                        <p className="text-[10px] text-gray-400 dark:text-[var(--color-text-muted)] text-center mt-0.5">
                          {formatMessageTime(message.timestamp || message.created_at)}
                        </p>
                      </div>
                    </motion.div>
                  </React.Fragment>
                );
              }
              
              const isOwnMessage = message.senderId === user?.id;
              const isSearchMatch = searchMatchIndex >= 0 && 
                searchMatches.length > 0 &&
                searchMatches[searchMatchIndex]?.message.id === message.id;
              
              const senderName = getSenderName(message);
              const senderAvatar = getSenderAvatar(message);
              const readStatus = getMessageReadStatus(message.id);

              return (
                <Fragment key={message.id}>
                  {showDateSeparator && (
                    <div className="flex justify-center items-center my-4">
                      <div className="px-3 py-1 bg-white/80 dark:bg-[var(--color-bg-elevated)] rounded-full border border-gray-200 dark:border-white/10 shadow-sm">
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          {format(messageDate, 'dd.MM.yyyy', { locale: dateLocale })}
                        </p>
                      </div>
                    </div>
                  )}
                  <motion.div
                    data-message-id={message.id}
                  ref={(el) => {
                    if (el) messageRefs.current[message.id] = el;
                  }}
                  initial={mountedGroupMsgIds.current.has(String(message.id)) ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  onAnimationComplete={() => mountedGroupMsgIds.current.add(String(message.id))}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
                >
                  <div className={`flex gap-2 ${message.type === 'poll' || message.type === 'contact' ? 'max-w-[85%]' : 'max-w-[70%]'} ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
                    {!isOwnMessage && (() => {
                      const sender = groupMembers.find(m => m.id === message.senderId);
                      const senderIsPlus = message.senderIsPlus !== undefined
                        ? message.senderIsPlus
                        : (sender?.isPlus || sender?.userRole === 'plus' || sender?.userRole === 'admin');
                      const senderWithPlus = sender ? { ...sender, isPlus: senderIsPlus } : { isPlus: senderIsPlus };
                      return (
                        <UserContextMenu user={senderWithPlus} wrapper="div">
                          <div className={`message-avatar flex-shrink-0 ${senderIsPlus ? 'plus-frame-glow rounded-full' : ''}`}>
                            <UserAvatar
                              userId={String(message.senderId)}
                              avatarUrl={senderAvatar}
                              name={senderName}
                              size={32}
                            />
                          </div>
                        </UserContextMenu>
                      );
                    })()}
                    <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                      {/* Sender name for group messages (not own messages) */}
                      {!isOwnMessage && (() => {
                        const sender = groupMembers.find(m => String(m.id) === String(message.senderId));
                        const senderIsPlus = message.senderIsPlus !== undefined
                          ? message.senderIsPlus
                          : (sender?.isPlus || sender?.userRole === 'plus' || sender?.userRole === 'admin');
                        const senderWithPlus = sender ? { ...sender, isPlus: senderIsPlus } : { isPlus: senderIsPlus };
                        const groupRole = sender?.role || null;
                        return (
                          <UserContextMenu user={senderWithPlus} className="text-xs mb-1 px-1 flex items-center gap-1.5">
                            <Username user={senderWithPlus} variant="subtle" plain />
                            {groupRole === 'owner' && (
                              <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-500/20 text-amber-400 leading-none">{t('groupChatView.ownerBadge')}</span>
                            )}
                            {groupRole === 'admin' && (
                              <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-[#3d8ef0]/20 text-[#3d8ef0] leading-none">{t('groupChatView.adminBadge')}</span>
                            )}
                          </UserContextMenu>
                        );
                      })()}
                      
                      {/* Forwarded Label */}
                      {message.isForwarded && message.forwardedFromSenderId !== user?.id && (
                        <div className="mb-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-500 flex items-center gap-1">
                          <ArrowRight className="w-3 h-3" />
                          <span>{t('groupChatView.forwardedLabel')}</span>
                        </div>
                      )}
                      
                      {/* Reply Preview */}
                      {message.replyTo && typeof message.replyTo === 'object' && message.replyTo.id && (
                        <div
                          className={`mb-1 px-3 py-2 bg-gray-100 dark:bg-[var(--color-bg-elevated)] rounded-lg border-l-4 ${
                            isOwnMessage 
                              ? 'border-slate-300 dark:border-white/20' 
                              : 'border-gray-400 dark:border-gray-500'
                          } max-w-[85%] cursor-pointer hover:opacity-80 transition-opacity`}
                          onClick={() => {
                            const repliedMessageElement = messageRefs.current[message.replyTo.id];
                            if (repliedMessageElement) {
                              repliedMessageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              repliedMessageElement.style.backgroundColor = 'var(--color-surface-hover)';
                              setTimeout(() => {
                                repliedMessageElement.style.backgroundColor = '';
                                repliedMessageElement.style.transition = 'background-color 0.5s ease';
                              }, 1500);
                            }
                          }}
                        >
                          <p className="text-xs mb-0.5">
                            {(() => {
                              const replySender = groupMembers.find(m => m.id === message.replyTo.senderId);
                              if (replySender) {
                                const replySenderIsPlus = replySender?.isPlus || replySender?.userRole === 'plus' || replySender?.userRole === 'admin';
                                const replySenderWithPlus = { ...replySender, isPlus: replySenderIsPlus };
                                return (
                                  <UserContextMenu user={replySenderWithPlus}>
                                    <Username user={replySenderWithPlus} variant="subtle" plain />
                                  </UserContextMenu>
                                );
                              }
                              // Fallback: try to find original message to get sender Plus status
                              const originalMessage = selectedGroupMessages?.find(m => m.id === message.replyTo.id);
                              if (originalMessage) {
                                const originalSenderWithPlus = getSenderWithPlus(originalMessage);
                                return (
                                  <Username user={originalSenderWithPlus} variant="subtle" plain />
                                );
                              }
                              // Last fallback: plain text
                              return (
                                <span className="text-gray-700 dark:text-white">{message.replyTo.senderName || t('groupChatView.unknownFallback')}</span>
                              );
                            })()}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 break-words">
                            {message.replyTo.text || t('groupChatView.deletedMessageFallback')}
                          </p>
                        </div>
                      )}
                      
                      {/* Deleted for everyone — placeholder */}
                      {message.is_deleted_for_everyone && (
                        <div className="flex items-center gap-2 py-1 px-1 my-0.5 select-none">
                          <div className="flex-1 h-px bg-white/[0.08]" />
                          <span className="text-[11px] italic text-[#4d6a8a] whitespace-nowrap">
                            {t('groupChatView.deletedForEveryone', { name: message.deleted_by_name })}
                          </span>
                          <div className="flex-1 h-px bg-white/[0.08]" />
                        </div>
                      )}

                      {!message.is_deleted_for_everyone && (() => {
                        const isCardType = ['note_share', 'note_collab_invite', 'event_share', 'event_cancelled', 'poll', 'contact', 'image', 'file'].includes(message.type);
                        return (
                      <div
                      data-message-bubble
                      className={`message-bubble rounded-2xl ${
                          isCardType
                            ? ''
                            : `px-4 py-2.5 ${isOwnMessage
                              ? 'rounded-br-sm bg-blue-100 dark:bg-blue-600 text-slate-900 dark:text-white'
                              : 'rounded-bl-sm bg-slate-100 dark:bg-[#060c18] text-slate-900 dark:text-white border border-slate-300/80 dark:border-slate-700/60'}`
                        } ${
                          isSearchMatch ? 'ring-2 ring-yellow-400 ring-offset-2 dark:ring-offset-[#0d1424]' : ''
                        }`}
                        onDoubleClick={() => {
                          if (message.type !== 'system' && !message.is_deleted_for_everyone) {
                            handleReplyMessage(message);
                          }
                        }}
                        onContextMenu={(e) => {
                          if (message.type === 'system') {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                          }
                          e.preventDefault();
                          e.stopPropagation();
                          setMessageContextMenu({
                            isOpen: true,
                            x: e.clientX,
                            y: e.clientY,
                            message: message,
                          });
                        }}
                      >
                        {message.type === 'text' && message.content && (
                          <p 
                            className={`message-text text-sm ${isOwnMessage ? 'text-slate-900 dark:text-white' : 'text-gray-900 dark:text-white'} whitespace-pre-wrap break-words`}
                          >
                            {renderMessageContent(message.content, messageSearchQuery, isOwnMessage)}
                          </p>
                        )}
                        {message.type === 'image' && (message.file_url || message.fileUrl) && (
                          <div className="space-y-2">
                            <ChatImage
                              fileUrl={message.fileUrl || message.file_url}
                              onClick={() => {
                                setSelectedImageUrl(message.fileUrl || message.file_url);
                                setSelectedImageFileName(message.file_name || message.fileName || 'image');
                                setShowImageModal(true);
                              }}
                            />
                            {message.content && 
                             message.content.trim() && 
                             message.content !== message.file_name && (
                              <p 
                                className={`message-text text-sm ${isOwnMessage ? 'text-slate-900 dark:text-white' : 'text-gray-900 dark:text-white'} whitespace-pre-wrap break-words`}
                              >
                                {renderMessageContent(message.content, messageSearchQuery, isOwnMessage)}
                              </p>
                            )}
                          </div>
                        )}
                        {message.type === 'file' && message.file_url && (
                          <div className="mt-2 flex items-center gap-2 p-2 bg-gray-100 dark:bg-[var(--color-bg-elevated)] rounded-lg">
                            <Paperclip className="w-4 h-4 text-gray-500 dark:text-[var(--color-text-secondary)]" />
                            <button
                              onClick={() => handleFileDownload(message.file_url, message.file_name || message.content)}
                              className="text-sm font-medium text-slate-900 dark:text-white hover:underline truncate text-left"
                            >
                              {fixFileName(message.file_name || message.content || t('groupChatView.fileDefaultName'))}
                            </button>
                          </div>
                        )}
                        {message.type === 'note_share' && (
                          <SharedNoteMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            groupId={group?.id}
                          />
                        )}
                        {message.type === 'note_collab_invite' && (
                          <CollabInviteMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            groupId={group?.id}
                            onAccepted={() => {}}
                          />
                        )}
                        {(message.type === 'event_share' || message.type === 'event_cancelled') && (
                          <EventCardMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            messageId={message.id}
                            chatType="group"
                            groupId={selectedGroupId}
                          />
                        )}
                        {message.type === 'poll' && (
                          <PollCardMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            messageId={message.id}
                            chatType="group"
                            groupId={selectedGroupId}
                          />
                        )}
                        {message.type === 'contact' && (
                          <ContactCardMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            onOpenFriendInfo={onContactOpenFriendInfo}
                            onAddFriend={onContactAddFriend}
                          />
                        )}
                      </div>
                        );
                      })()}
                      
                      {/* Reactions */}
                      {!message.is_deleted_for_everyone && messageReactions[message.id] && Object.keys(messageReactions[message.id]).length > 0 && (
                        <div className={`flex flex-wrap gap-1 mt-1.5 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          {Object.entries(messageReactions[message.id]).map(([emoji, count]) => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => handleAddReaction(emoji, message.id)}
                              className="flex items-center gap-1 bg-[#161f31] hover:bg-[#24354f] border border-white/[0.1] rounded-full px-2 py-0.5 transition-colors"
                            >
                              <TwemojiText text={emoji} className="text-base" />
                              <span className="text-[var(--color-text-secondary)] text-xs ml-0.5">{count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="message-meta flex items-center gap-1 mt-1 px-1">
                        <span className="message-time text-xs text-gray-500 dark:text-gray-500">
                          {formatMessageTime(message.timestamp || message.created_at)}
                        </span>
                        {/* Read receipts - only for own messages (WhatsApp-style) */}
                        {isOwnMessage && (
                          <div
                            className={`message-ticks ${
                              readStatus.allRead
                                ? 'double read'
                                : readStatus.readCount > 0
                                ? 'double'
                                : 'single'
                            }`}
                          >
                            {(readStatus.readCount > 0 || readStatus.allRead) && (
                              <span className="tick tick-back">✓</span>
                            )}
                            <span className="tick tick-front">✓</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
                </Fragment>
              );
            })
          )}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Upload progress bar - above input */}
      {uploadProgress != null && (
        <div className="px-4 pt-2 z-10">
          <div className="h-1.5 w-full bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-blue-500 dark:bg-blue-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${uploadProgress}%` }}
              transition={{ duration: 0.2 }}
            />
          </div>
        </div>
      )}
      {/* Message Input Area */}
      <div className="relative p-4 border-t border-gray-200 dark:border-white/10 bg-white/90 dark:bg-[var(--color-bg-base)] backdrop-blur-sm z-10">
        {/* Reply Preview */}
        {replyToMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-3 p-3 bg-slate-100 dark:bg-[var(--color-bg-elevated)] border-l-4 border-slate-300 dark:border-white/20 rounded-lg flex items-start gap-3"
          >
            <Reply className="w-4 h-4 text-[#3d8ef0] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-700 dark:text-white mb-1">
                {replyToMessage.sender ? (
                  <Username user={replyToMessage.sender} variant="subtle" plain />
                ) : (
                  replyToMessage.senderName
                )}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 break-words">
                {replyToMessage.text}
              </p>
            </div>
            <button
              onClick={() => setReplyToMessage(null)}
              className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-[var(--color-bg-elevated)] text-gray-500 dark:text-[var(--color-text-secondary)] hover:text-gray-700 dark:hover:text-white transition-colors"
              title={t('groupChatView.cancelReply')}
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
        
        {/* @Mention Autocomplete */}
        {showMentionList && filteredMembersForMention.length > 0 && (
          <div className="mb-3 p-2 bg-white dark:bg-[var(--color-bg-elevated)] border border-gray-200 dark:border-white/10 rounded-xl shadow-lg max-h-48 overflow-y-auto">
            {filteredMembersForMention.map((member) => (
              <button
                key={member.id}
                type="button"
                onClick={() => handleMentionSelect(member)}
                className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#24354f]/50 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center text-xs font-semibold text-slate-900 dark:text-white border border-slate-200 dark:border-white/10 flex-shrink-0">
                  {member.avatarUrl ? (
                    <img
                      src={member.avatarUrl}
                      alt={getDisplayName(member)}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    getInitials(getDisplayName(member))
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">
                    <Username user={member} variant="default" plain />
                  </p>
                  {member.email && (
                    <p className={`text-xs truncate ${getPlusEmailColor(member)}`}>
                      {member.email}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
        
        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          {/* Action Buttons */}
          <div className="flex items-center gap-1">
            <EmojiPickerButton
              onEmojiSelect={(emoji) => {
                if (isGroupLocked || isGroupDestroyed) return;
                inputRef.current?.insertEmoji(emoji);
              }}
              disabled={isGroupLocked || isGroupDestroyed}
              className="p-2.5 rounded-xl hover:bg-[#1e2d47] text-[#7a96b0] hover:text-white"
            />
            <motion.button
              type="button"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleFileSelect('image')}
              disabled={isGroupLocked || isGroupDestroyed}
              className={`p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-[var(--color-text-secondary)] hover:text-gray-900 dark:hover:text-white transition-colors ${
                isGroupLocked || isGroupDestroyed ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title={isGroupDestroyed ? t('groupChatView.groupDestroyedShort') : isGroupLocked ? t('groupChatView.groupLockedShort') : t('groupChatView.photoTitle')}
            >
              <Image className="w-5 h-5" />
            </motion.button>
            <div ref={attachmentTriggerRef} data-chat-attach-trigger="true">
              <motion.button
                type="button"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowAttachmentPopover((v) => !v)}
                disabled={isGroupLocked || isGroupDestroyed}
                className={`p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-[var(--color-text-secondary)] hover:text-gray-900 dark:hover:text-white transition-colors ${
                  isGroupLocked || isGroupDestroyed ? 'opacity-50 cursor-not-allowed' : ''
                } ${showAttachmentPopover ? 'bg-gray-100 dark:bg-[var(--color-bg-elevated)]' : ''}`}
                title={isGroupDestroyed ? t('groupChatView.groupDestroyedShort') : isGroupLocked ? t('groupChatView.groupLockedShort') : t('groupChatView.attachmentsTitle')}
              >
                <Plus className="w-5 h-5" />
              </motion.button>
            </div>
            <ChatAttachmentPopover
              isOpen={showAttachmentPopover}
              onClose={() => setShowAttachmentPopover(false)}
              anchorRef={attachmentTriggerRef}
              disabled={isGroupLocked || isGroupDestroyed}
              onShareNote={() => setShowNoteListModal(true)}
              onSharePhoto={() => handleFileSelect('image')}
              onShareFile={() => handleFileSelect('file')}
              onCreateEvent={() => {
                setShowAttachmentPopover(false);
                const count = calendars?.length ?? 0;
                if (count === 0) {
                  window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                    detail: { type: 'info', title: t('groupChatView.calendarRequiredTitle'), message: t('groupChatView.calendarRequiredMessage') },
                  }));
                } else if (count === 1) {
                  setShowCreateEventModal(true);
                } else {
                  setShowSelectCalendarModal(true);
                }
              }}
              onCreatePoll={() => {
                setShowAttachmentPopover(false);
                setShowPollCreateModal(true);
              }}
              onShareContact={() => {
                setShowAttachmentPopover(false);
                setShowContactShareModal(true);
              }}
            />
          </div>

          {/* Group Locked/Destroyed Warning */}
          {(isGroupLocked || isGroupDestroyed) && (
            <div className={`w-full mb-2 p-3 border rounded-xl ${
              isGroupDestroyed 
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
            }`}>
              <p className={`text-xs text-center ${
                isGroupDestroyed 
                  ? 'text-red-700 dark:text-red-300' 
                  : 'text-amber-700 dark:text-amber-300'
              }`}>
                {isGroupDestroyed
                  ? t('groupChatView.destroyedMessageSocket')
                  : (groupLockedStatus?.message || group?.lockMessage || t('groupChatView.lockedMessageSocket'))
                }
              </p>
            </div>
          )}

          {/* Text Input */}
          <div
            className="flex-1 relative min-w-0"
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const hasSelectedText = inputRef.current &&
                inputRef.current.getSelectionStart() !== inputRef.current.getSelectionEnd();
              setTextAreaContextMenu({
                isOpen: true,
                x: e.clientX,
                y: e.clientY,
                hasSelection: hasSelectedText,
              });
            }}
          >
            <TwemojiInput
              ref={inputRef}
              onTextChange={handleTextChange}
              onKeyDown={handleKeyDown}
              onEnterPress={handleSendMessage}
              placeholder={
                isGroupDestroyed
                  ? t('groupChatView.placeholderDestroyed')
                  : isGroupLocked
                  ? t('groupChatView.placeholderLocked')
                  : t('groupChatView.placeholderDefault')
              }
              className={isGroupLocked || isGroupDestroyed ? 'opacity-50 pointer-events-none' : ''}
            />
          </div>

          {/* Send Button */}
          <motion.button
            type="submit"
            whileHover={messageText.trim() && !isGroupLocked && !isGroupDestroyed ? { scale: 1.05 } : {}}
            whileTap={messageText.trim() && !isGroupLocked && !isGroupDestroyed ? { scale: 0.95 } : {}}
            disabled={!messageText.trim() || isGroupLocked || isGroupDestroyed}
            className={`flex-shrink-0 p-3 rounded-xl transition-all duration-200 ${
              messageText.trim() && !isGroupLocked
                ? 'bg-slate-100 dark:bg-[var(--color-bg-elevated)] text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-[#24354f]'
                : 'bg-gray-200 dark:bg-[var(--color-bg-elevated)] text-gray-400 dark:text-[var(--color-text-secondary)] cursor-not-allowed'
            }`}
            title={isGroupLocked ? t('groupChatView.sendButtonLocked') : t('groupChatView.sendButtonTitle')}
          >
            <Send className="w-5 h-5" />
          </motion.button>
        </form>
      </div>

      {/* Modals */}
      <ImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImageUrl(null);
          setSelectedImageFileName(null);
        }}
        imageUrl={selectedImageUrl}
        fileName={selectedImageFileName}
      />

      <ImagePreviewModal
        isOpen={showImagePreviewModal}
        onClose={() => {
          setShowImagePreviewModal(false);
          setSelectedImageFile(null);
        }}
        imageFile={selectedImageFile}
        onSend={handleSendImage}
      />

      <FileConfirmModal
        isOpen={showFileConfirmModal}
        onClose={() => {
          setShowFileConfirmModal(false);
          setPendingFile(null);
        }}
        file={pendingFile}
        onConfirm={async (file) => {
          await uploadWithType('file', file);
          setShowFileConfirmModal(false);
          setPendingFile(null);
        }}
        onCancel={() => {
          setShowFileConfirmModal(false);
          setPendingFile(null);
        }}
      />

      <MessageInfoModal
        isOpen={showMessageInfoModal}
        onClose={() => {
          setShowMessageInfoModal(false);
          setSelectedMessageForInfo(null);
        }}
        message={selectedMessageForInfo}
        isGroupMessage={true}
        readStatus={selectedMessageForInfo ? getMessageReadStatus(selectedMessageForInfo.id) : null}
        groupMembers={groupMembers}
      />

      <ForwardModal
        isOpen={showForwardModal}
        onClose={() => {
          setShowForwardModal(false);
          setForwardMessage(null);
        }}
        message={forwardMessage}
        isGroupMessage={true}
      />

      <NoteListModal
        isOpen={showNoteListModal}
        onClose={() => setShowNoteListModal(false)}
        shareTarget={group?.id ? { type: 'group', groupId: group.id } : null}
        onShareSuccess={() => {
          if (selectedGroupId) loadGroupMessages(selectedGroupId);
        }}
      />

      <SelectCalendarModal
        isOpen={showSelectCalendarModal}
        onClose={() => setShowSelectCalendarModal(false)}
        calendars={calendars || []}
        onSelect={(cal) => {
          setSelectedCalendarForEvent(cal);
          setShowSelectCalendarModal(false);
          setShowCreateEventModal(true);
        }}
      />
      <CreateEventModal
        isOpen={showCreateEventModal}
        onClose={() => { setShowCreateEventModal(false); setSelectedCalendarForEvent(null); }}
        calendar={selectedCalendarForEvent || selectedCalendar || calendars?.[0]}
        onCreate={async (eventData) => {
          try {
            const savedEvent = await createEvent(eventData);
            if (!savedEvent) return;
            const payload = {
              event_id: savedEvent.id,
              event_title: savedEvent.title,
              event_start: savedEvent.startDate,
              event_end: savedEvent.endDate,
              event_all_day: savedEvent.allDay ?? false,
              event_description: savedEvent.description ?? null,
              event_location: eventData.location ?? null,
            };
            sendGroupMessage(selectedGroupId, '', 'event_share', null, null, null, null, [], payload);
            setShowCreateEventModal(false);
          } catch (err) {
            console.error('[GroupChatView] Create event failed:', err);
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: { type: 'error', title: t('groupChatView.eventCreateFailedTitle'), message: getApiErrorMessage(err, { fallback: t('groupChatView.eventCreateFailedFallback') }) },
            }));
          }
        }}
      />

      <PollCreateModal
        isOpen={showPollCreateModal}
        onClose={() => setShowPollCreateModal(false)}
        onSubmit={(payload) => {
          sendGroupMessage(selectedGroupId, '', 'poll', null, null, null, null, [], null, payload);
          setShowPollCreateModal(false);
        }}
      />

      <ContactShareModal
        isOpen={showContactShareModal}
        onClose={() => setShowContactShareModal(false)}
        onSubmit={(payload) => {
          sendGroupMessage(selectedGroupId, '', 'contact', null, null, null, null, [], null, null, payload);
          setShowContactShareModal(false);
        }}
      />

      {/* Context Menus */}
      <ContextMenu
        isOpen={textAreaContextMenu.isOpen}
        position={{ x: textAreaContextMenu.x, y: textAreaContextMenu.y }}
        items={[
          {
            label: t('groupChatView.contextMenu.cut'),
            icon: Scissors,
            onClick: () => {
              if (inputRef.current) {
                const selectedText = inputRef.current.getSelectedText();
                if (selectedText) {
                  navigator.clipboard?.writeText(selectedText).catch(() => {});
                  inputRef.current.deleteSelection();
                }
              }
              setTextAreaContextMenu({ isOpen: false, x: 0, y: 0 });
            },
            disabled: !textAreaContextMenu.hasSelection,
          },
          {
            label: t('groupChatView.contextMenu.copy'),
            icon: Copy,
            onClick: () => {
              if (inputRef.current) {
                const selectedText = inputRef.current.getSelectedText();
                if (selectedText) {
                  navigator.clipboard?.writeText(selectedText).catch(() => {});
                }
              }
              setTextAreaContextMenu({ isOpen: false, x: 0, y: 0 });
            },
            disabled: !textAreaContextMenu.hasSelection,
          },
          {
            label: t('groupChatView.contextMenu.paste'),
            icon: Clipboard,
            onClick: async () => {
              if (inputRef.current) {
                try {
                  const text = await navigator.clipboard.readText();
                  inputRef.current.insertAtCursor(text);
                } catch (error) {
                  console.error('Paste failed:', error);
                }
              }
              setTextAreaContextMenu({ isOpen: false, x: 0, y: 0 });
            },
          },
        ]}
        onClose={() => setTextAreaContextMenu({ isOpen: false, x: 0, y: 0, hasSelection: false })}
      />

      <ContextMenu
        isOpen={messageContextMenu.isOpen && messageContextMenu.message?.type !== 'system'}
        position={{ x: messageContextMenu.x, y: messageContextMenu.y }}
        header={
          <div className="flex items-center gap-0.5 px-2 py-2">
            {QUICK_REACTIONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => handleAddReaction(emoji)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.08] hover:scale-125 transition-all duration-150 text-lg"
              >
                <TwemojiText text={emoji} />
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                const msg = messageContextMenu.message;
                setReactionPickerState({ isOpen: true, x: messageContextMenu.x, y: messageContextMenu.y, messageId: msg?.id });
                setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
              }}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.08] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        }
        items={[
          ...(messageContextMenu.message?.senderId === user?.id && messageContextMenu.message?.type !== 'system'
            ? [
                {
                  label: t('groupChatView.messageMenu.info'),
                  icon: Info,
                  onClick: handleShowMessageInfo,
                },
              ]
            : []),
          {
            label: t('groupChatView.messageMenu.reply'),
            icon: Reply,
            onClick: handleReplyMessage,
            disabled: messageContextMenu.message?.type === 'system',
          },
          {
            label: t('groupChatView.messageMenu.copy'),
            icon: Copy,
            onClick: handleCopyMessage,
            disabled: messageContextMenu.message?.type === 'system',
          },
          {
            label: t('groupChatView.messageMenu.forward'),
            icon: Share2,
            onClick: handleForwardMessage,
            disabled: messageContextMenu.message?.type === 'system',
          },
          {
            label: messageContextMenu.message?.is_starred || messageContextMenu.message?.starred ? t('groupChatView.messageMenu.unstar') : t('groupChatView.messageMenu.star'),
            icon: Star,
            onClick: handleStarMessage,
            disabled: messageContextMenu.message?.type === 'system',
          },
          // Sil — sistem mesajı hariç, henüz silinmemiş
          ...(messageContextMenu.message?.type !== 'system' && !messageContextMenu.message?.is_deleted_for_everyone
            ? [{
                label: t('groupChatView.messageMenu.delete'),
                icon: Trash2,
                onClick: openDeleteFlow,
                danger: true,
              }]
            : []),
          // Mesajı Bildir — yalnız başkasının (sistem değil)
          ...(messageContextMenu.message?.type !== 'system' &&
            messageContextMenu.message?.senderId !== user?.id &&
            String(messageContextMenu.message?.senderId) !== String(user?.id) &&
            !messageContextMenu.message?.is_deleted_for_everyone
            ? [{
                label: t('groupChatView.messageMenu.report'),
                icon: Flag,
                onClick: () => {
                  const msg = messageContextMenu.message;
                  setReportModal({ isOpen: true, messageId: msg?.id || null });
                },
              }]
            : []),
        ]}
        onClose={() => setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null })}
      />

      <ReportMessageModal
        isOpen={reportModal.isOpen}
        messageId={reportModal.messageId}
        onClose={() => setReportModal({ isOpen: false, messageId: null })}
      />

      {/* Reaction full emoji picker */}
      {reactionPickerState.isOpen && (
        <ReactionEmojiPicker
          x={Math.min(reactionPickerState.x, window.innerWidth - 360)}
          y={Math.min(reactionPickerState.y, window.innerHeight - 420)}
          messageId={reactionPickerState.messageId}
          onSelect={(emoji, msgId) => handleAddReaction(emoji, msgId)}
          onClose={() => setReactionPickerState({ isOpen: false, x: 0, y: 0, messageId: null })}
        />
      )}

      {/* Delete message — choose type modal */}
      <AnimatePresence>
        {deleteFlow.chooseOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000]"
              onClick={() => setDeleteFlow({ chooseOpen: false, confirmOpen: false, message: null, deleteType: null })}
            />
            <motion.div className="fixed inset-0 flex items-center justify-center z-[100001] p-4" onClick={(e) => e.stopPropagation()}>
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 8 }}
                transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                className="w-full max-w-sm bg-[var(--color-bg-base)] rounded-2xl border border-white/[0.08] shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 p-6 pb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-5 h-5 text-red-400" strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{t('groupChatView.deleteChoice.title')}</h3>
                </div>
                <div className="px-6 pb-6 space-y-2">
                  <p className="text-sm text-[var(--color-text-muted)] mb-4">{t('groupChatView.deleteChoice.question')}</p>
                  {deleteFlow.message?.senderId === user?.id && (
                    <button
                      onClick={() => chooseDeleteType('for_everyone')}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 text-sm font-medium transition-colors text-left"
                    >
                      <Trash2 className="w-4 h-4 flex-shrink-0" />
                      <div>
                        <div className="font-semibold">{t('groupChatView.deleteChoice.forEveryone')}</div>
                        <div className="text-xs text-red-400/70 mt-0.5">{t('groupChatView.deleteChoice.forEveryoneHint')}</div>
                      </div>
                    </button>
                  )}
                  <button
                    onClick={() => chooseDeleteType('for_me')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.08] text-[var(--color-text-secondary)] text-sm font-medium transition-colors text-left"
                  >
                    <Trash2 className="w-4 h-4 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">{t('groupChatView.deleteChoice.forMe')}</div>
                      <div className="text-xs text-[#4d6a8a] mt-0.5">{t('groupChatView.deleteChoice.forMeHint')}</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setDeleteFlow({ chooseOpen: false, confirmOpen: false, message: null, deleteType: null })}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-medium text-[var(--color-text-muted)] hover:bg-white/[0.04] transition-colors mt-1"
                  >
                    {t('groupChatView.deleteChoice.cancel')}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete message — confirm modal */}
      <DeleteModal
        isOpen={deleteFlow.confirmOpen}
        onClose={() => setDeleteFlow({ chooseOpen: false, confirmOpen: false, message: null, deleteType: null })}
        onConfirm={handleConfirmDelete}
        title={deleteFlow.deleteType === 'for_everyone' ? t('groupChatView.deleteConfirm.forEveryoneTitle') : t('groupChatView.deleteConfirm.forMeTitle')}
        message={
          deleteFlow.deleteType === 'for_everyone'
            ? t('groupChatView.deleteConfirm.forEveryoneMessage')
            : t('groupChatView.deleteConfirm.forMeMessage')
        }
      />
    </div>
  );
};

export default GroupChatView;
